import { centsToDollar } from '@givebacks/utils/currency';

import { Card, CardProps, ImageMantine } from '../../../../DataDisplay';
import { Skeleton } from '../../../../Feedback';
import { Group, Stack } from '../../../../Layout';
import { Divider } from '../../../../Miscellaneous';
import { Box } from '../../../../Miscellaneous/Box';
import { Text } from '../../../../Typography';
import { CartItem } from '../CartCard';
import classes from './ReceiptBreakdownCard.module.css';
import receiptBottom from './receiptBottom.svg';
import receiptTop from './receiptTop.svg';

type ConditionalTotalsProps =
  | {
      hideTotals?: true;
      subtotal?: never;
      fees?: never;
      total?: never;
    }
  | {
      hideTotals?: false;
      subtotal: number;
      fees?: number;
      total: number;
    };

type ReceiptBreakdownCardProps = ConditionalTotalsProps &
  CardProps & {
    imgUrl?: string;
    causeName: string;
    taxId?: string;
    websiteUrl?: string;
  };

const ReceiptBreakdownCard = ({
  imgUrl,
  causeName,
  taxId,
  websiteUrl,
  hideTotals = false,
  subtotal,
  fees,
  total,
  ...props
}: ReceiptBreakdownCardProps) => {
  return (
    <Card className={classes['ReceiptBreakdown__Card']} {...props}>
      <Group className={classes['ReceiptBreakdown__Card-header']}>
        <ImageMantine className={classes['ReceiptBreakdown__Card-image']} src={imgUrl} />
        <Stack gap={4}>
          <Text fz="sm" fw={600} c="gray.8">
            {causeName}
          </Text>
          {taxId && (
            <Text fz="xs" c="gray">
              EIN: {taxId}
            </Text>
          )}
          {websiteUrl && (
            <Text fz="xs" c="gray">
              {websiteUrl}
            </Text>
          )}
        </Stack>
      </Group>

      {!hideTotals && (
        <>
          <Divider />
          <Stack gap={4} p="md">
            <Group gap="sm" justify="space-between">
              <Text fz="sm" c="gray.8">
                Subtotal
              </Text>
              <Text fz="sm" fw={600} c="gray.8">
                {centsToDollar(subtotal)}
              </Text>
            </Group>

            <Group gap="sm" justify="space-between">
              <Text fz="sm" c="gray.8">
                Fees
              </Text>
              <Text fz="sm" fw={600} c="gray.8">
                {centsToDollar(fees)}
              </Text>
            </Group>

            <Group gap="sm" justify="space-between">
              <Text fz="sm" c="gray.8" fw={600}>
                Cart Total
              </Text>
              <Text fz="sm" fw={600} c="gray.8">
                {centsToDollar(total)}
              </Text>
            </Group>
          </Stack>
        </>
      )}
    </Card>
  );
};

const Receipt = ({
  children,
  isLoading = false,
}: {
  children: React.ReactNode;
  isLoading?: boolean;
}) => {
  return (
    <Box className={classes.Receipt}>
      <Box className={classes.Receipt__contentOverlay} />
      <ImageMantine src={receiptTop} className={classes.Receipt__top} />
      <Stack gap="lg" mb={4} className={classes.Receipt__content}>
        {isLoading ? (
          <>
            <Skeleton visible h={80} radius="sm" />
            <Skeleton visible h={186} radius="sm" />
          </>
        ) : (
          children
        )}
      </Stack>
      <ImageMantine src={receiptBottom} className={classes.Receipt__bottom} />
    </Box>
  );
};

const ReceiptCart = ({ children }: { children: React.ReactNode }) => {
  return (
    <Card className={classes.Receipt__cart}>
      <Stack gap={1}>{children}</Stack>
    </Card>
  );
};

Receipt.Breakdown = ReceiptBreakdownCard;
Receipt.Cart = ReceiptCart;
Receipt.Item = CartItem;

export { Receipt };
